/**
 * Built by SHO on 10/05/2019.
*/

import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { MathHelper } from "../utils/MathHelper";
import { Helper } from "../utils/Helper";
import {  gsap } from "gsap";

export interface ISequenceConfig {
    textures?:PIXI.Texture[];
    texturePath?:string;
    reverse?:boolean;
    clips?:ISequenceClip[];
}

export interface ISequenceClip {
    name:string;
    from?:number;
    to?:number;
}

export interface ISequencePlay {
    duration?:number;
    clip?:string;
    reverse?:boolean;
    repeat?:number;
    speedFactor?:number;
    forceEnd?:boolean;
    frameEase?:gsap.EaseFunction;
    onCompleteCallback?:() => void;
    onUpdate?:() => void;
    onStart?:() => void;
    onIndex?: number;
}

export class Sequence extends PIXI.Sprite {

    private static assetLoader: AssetLoader;

    protected _config:ISequenceConfig;
    private _timeline:gsap.core.Timeline | undefined;
    private _frameEase:gsap.EaseFunction;
    
    private timeScaling:number = 1;

    public static createSequence(texture?:PIXI.Texture[]):Sequence{
        const seq:Sequence =  new Sequence({ textures: texture ? texture :  [] });
        seq.anchor.set(0.5,0.5);
        return seq;
    }

    public static setAssetLoader(_assetLoader:AssetLoader){
        this.assetLoader = _assetLoader;
    }

    public get timeline():gsap.core.Timeline | undefined{
        return this._timeline;
    }

    public get isActive():boolean {
        return this._timeline != undefined && this._timeline!.isActive();
    }

    public get totalFrames():number {
        return this._config.textures!.length;
    }

    public get totalDuration():number {
        return this.totalFrames*MathHelper.frameToSeconds(1);
    }

    public get isReverse():boolean{
        return !!this._config.reverse;
    }

    public set isReverse(value:boolean){
        this._config.reverse = value;
    }

    public get frameDuration():number {
        return this.totalDuration / this.totalFrames;
    }

    public get firstFrame():PIXI.Texture {
        return this.textures[0];
    }

    public get lastFrame():PIXI.Texture {
        return this.textures[this.totalFrames-1];
    }

    public get textures():PIXI.Texture[]{
        return this._config.textures!;
    }
   
    public get currentFrame():number {
        if(!this._timeline) return 0;
        const frame:number = Math.floor(this.totalFrames * (this._frameEase ? this._frameEase(this._timeline.progress()) : this._timeline.progress() ));
        return Math.max(0, Math.min(frame, this.totalFrames - 1));
    }
   
    public getFrame(value?:number):PIXI.Texture {
        return this.textures[ (value == undefined ? this.currentFrame : value) ];
    }

    public get currentFrameTexture():PIXI.Texture{
        return this.texture;
    }

    public set timeScale(value:number){
        this.timeScaling = value;
    }

    protected get clips():ISequenceClip[] | undefined{
        return this._config.clips;
    }

    constructor(config?:ISequenceConfig) {
        super();
        this._config = config ? config : { textures:[] };
        if(this._config?.texturePath) this.setTexture(this._config.texturePath);
        this.renderFrame();
    }

    public setFrame(frame:number):void{
        this.texture = this.textures[frame];
    }

    public setTexture(newTextures:string | PIXI.Texture[]):void{
        if(typeof newTextures ===  "string")
            this._config.textures = Sequence.assetLoader.getTextures(newTextures);
        else
            this._config.textures = newTextures;

        this.renderFrame();
    }

    public play(config:ISequencePlay = {}):gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline!, new gsap.core.Timeline({ 
            onStart: config.onStart,
            onComplete: config.onCompleteCallback,
            repeat: config.repeat,
            repeatDelay: 0
         }),!!config.forceEnd);

        let duration:number = this.totalDuration;
        this.setFrame(config.reverse ? this.totalFrames-1 : 0);
        this._frameEase = config.frameEase ? config.frameEase : undefined!;

        if(config.clip){
            const clip:ISequenceClip | undefined = this.findClip(config.clip);
            if(clip){
                if(clip.to === undefined) clip.to = this.totalFrames-1;
                if(clip.from === undefined)   clip.from = 0;

                let amountOfFrames:number = Math.abs(clip.to - clip.from);
                const reversedFrames:boolean = clip.from > clip.to;

                let startFrame:number = config.reverse ? clip.to : clip.from;

                duration = config.duration === undefined ? amountOfFrames*MathHelper.frameToSeconds(1) : config.duration;
                duration *= config.speedFactor ? config.speedFactor : 1;

                this.setFrame(startFrame);
                this._timeline.to(this, {
                    duration:duration,
                    onUpdate : () => { 
                        config.onUpdate && config.onUpdate();
                        const progressFrame:number = Math.floor(amountOfFrames * this._timeline!.progress());
                        const frame:number = startFrame + (config.reverse || this.isReverse || reversedFrames ? -progressFrame : progressFrame);
                        this.texture = this.textures[frame];
                    },
                    onComplete :() => {
                        const frame:number = startFrame + (config.reverse || this.isReverse || reversedFrames ? -amountOfFrames : amountOfFrames);
                        this.texture = this.textures[frame];
                    }
                });

            }else
                Logger.warn("Missing clip: " + config.clip + ".");
        }else{
            duration = config.duration === undefined ? this.totalDuration : config.duration;
            duration *= config.speedFactor ? config.speedFactor : 1;
            this._timeline.to(this, {
                duration: duration,
                onUpdate : () => { 
                    config.onUpdate && config.onUpdate();
                    this.renderFrame(config.reverse);
                },
                onComplete :() => this.renderFrame()
            });
            this._timeline.play();
        }
        this._timeline.timeScale(this.timeScaling);
        return this._timeline;
    }

    public reset(playEnd?:boolean):void{
        this._timeline = Helper.setTimeline(this._timeline!, undefined, playEnd)
    }

    public pause():void{ 
        if(this._timeline) this._timeline.pause();
    }

    public resume():void{ 
        if(this._timeline) this._timeline.play();
    }

    public stop(playToEnd:boolean = false):void{ 
        if(this._timeline && this._timeline.isActive() && playToEnd){
            this._timeline.progress(1);
            this._timeline!.eventCallback("onComplete")();
        } 
        this._timeline = Helper.setTimeline(this._timeline);
    }

    protected findClip(SearchName:string):ISequenceClip | undefined{
        return this.clips ? this.clips.find(clip => clip.name == SearchName) : undefined;
    }

    public setClips(clips:ISequenceClip[]):void{
        this._config.clips = clips;
    }

    private renderFrame(reverse?:boolean):void {
        let frame:number = this.isReverse || reverse? (this.totalFrames-1) - this.currentFrame : this.currentFrame;
        this.texture = this.textures[frame];
    }
  
    public setVisible(value:boolean):void {
        this.visible = value;
        this.alpha =  value ? 1 : 0;
    }

    public resetToDefault():void{
        this.setFrame(this._config.reverse? this.textures.length-1 :  0);
        this.pivot.set(0,0);
        this.scale.set(1,1);
        this.blendMode = PIXI.BLEND_MODES.NORMAL;
    }
}